import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  createSpaRetailSale,
  getSpaInventoryLocations,
  getSpaInventoryProducts,
  getSpaInventoryStock,
  getSpaRetailSales,
  spaDisplayName,
} from "../APIs/SpaAPI";
import { useSnackbar } from "../contexts/SnackBarContext";

const emptyItem = () => ({ productId: "", quantity: 1 });
const emptySale = () => ({
  locationId: "",
  sellingOperatorId: "",
  clientId: "",
  items: [emptyItem()],
  discountPercent: 0,
  discountAmount: 0,
});

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;
const personLabel = (person, fallback) => person?.label || spaDisplayName(person, fallback);
const saleDiscount = (sale) => Math.max(0, (sale.items || []).reduce((sum, item) => (
  sum + Number(item.unitPrice || 0) * Number(item.quantity || 0)
), 0) - Number(sale.total || 0));

function SpaRetailSales({ token, tenant, canManage, canViewResources, clients = [], operators = [] }) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const rt = useCallback((key, defaultValue, options = {}) => (
    t(`spa.finance.retail.${key}`, { defaultValue, ...options })
  ), [t]);
  const [form, setForm] = useState(emptySale);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadData = useCallback(async (options = {}) => {
    if (!token || !tenant) return;
    setLoading(true);
    setError("");
    try {
      const [saleData, resourceData] = await Promise.all([
        getSpaRetailSales({ limit: 200 }, token, tenant, { signal: options.signal }),
        canViewResources
          ? Promise.all([
            getSpaInventoryLocations(token, tenant, { signal: options.signal }),
            getSpaInventoryProducts(token, tenant, { signal: options.signal }),
          ])
          : Promise.resolve([[], []]),
      ]);
      if (options.signal?.aborted) return;
      const [locationData, productData] = resourceData;
      const activeLocations = locationData.filter((location) => location.active !== false);
      setSales(Array.isArray(saleData) ? saleData : []);
      setLocations(activeLocations);
      setProducts(Array.isArray(productData) ? productData : []);
      setForm((current) => ({
        ...current,
        locationId: activeLocations.some((location) => String(location.id) === String(current.locationId))
          ? current.locationId
          : (activeLocations[0]?.id ? String(activeLocations[0].id) : ""),
      }));
    } catch (loadError) {
      if (loadError?.name !== "AbortError" && !options.signal?.aborted) {
        setError(loadError?.message || rt("messages.loadError", "Unable to load SPA retail sales."));
      }
    } finally {
      if (!options.signal?.aborted) setLoading(false);
    }
  }, [canViewResources, rt, tenant, token]);

  useEffect(() => {
    const controller = new AbortController();
    loadData({ signal: controller.signal });
    return () => controller.abort();
  }, [loadData]);

  useEffect(() => {
    if (!canViewResources || !form.locationId || !token || !tenant) {
      setStock([]);
      return undefined;
    }
    const controller = new AbortController();
    setStockLoading(true);
    getSpaInventoryStock({ locationId: Number(form.locationId) }, token, tenant, { signal: controller.signal })
      .then((rows) => { if (!controller.signal.aborted) setStock(rows); })
      .catch((loadError) => {
        if (loadError?.name !== "AbortError" && !controller.signal.aborted) {
          setStock([]);
          setError(loadError?.message || rt("messages.stockError", "Unable to load stock."));
        }
      })
      .finally(() => { if (!controller.signal.aborted) setStockLoading(false); });
    return () => controller.abort();
  }, [canViewResources, form.locationId, rt, tenant, token]);

  const productById = useMemo(() => new Map(products.map((product) => [Number(product.id), product])), [products]);
  const stockByProduct = useMemo(() => new Map(stock.map((row) => [Number(row.product?.id), Number(row.quantity || 0)])), [stock]);
  const selectedProductIds = useMemo(() => form.items.map((item) => Number(item.productId)).filter(Boolean), [form.items]);
  const totals = useMemo(() => {
    const gross = form.items.reduce((sum, item) => {
      const product = productById.get(Number(item.productId));
      return sum + Number(product?.sellingPrice || 0) * Number(item.quantity || 0);
    }, 0);
    const percentDiscount = gross * Number(form.discountPercent || 0) / 100;
    const subtotal = Math.max(0, gross - percentDiscount);
    return {
      gross,
      percentDiscount,
      subtotal,
      total: Math.max(0, subtotal - Number(form.discountAmount || 0)),
    };
  }, [form.discountAmount, form.discountPercent, form.items, productById]);

  const validationError = useMemo(() => {
    if (!form.locationId) return rt("validation.location", "Select a stock location.");
    if (!form.sellingOperatorId) return rt("validation.operator", "Select the selling operator.");
    if (!form.items.length || form.items.some((item) => !item.productId || Number(item.quantity) <= 0)) {
      return rt("validation.items", "Select at least one product and enter positive quantities.");
    }
    if (new Set(selectedProductIds).size !== selectedProductIds.length) return rt("validation.duplicates", "Each product may be added only once.");
    if (Number(form.discountPercent) < 0 || Number(form.discountPercent) > 100) return rt("validation.discountPercent", "Discount percentage must be between 0 and 100.");
    if (Number(form.discountAmount) < 0 || Number(form.discountAmount) > totals.subtotal) return rt("validation.discountAmount", "Discount amount cannot exceed the discounted subtotal.");
    const insufficient = form.items.find((item) => Number(item.quantity) > (stockByProduct.get(Number(item.productId)) || 0));
    if (insufficient) {
      const product = productById.get(Number(insufficient.productId));
      return rt("validation.stock", "Insufficient stock for {{product}}.", { product: product?.name || `#${insufficient.productId}` });
    }
    return "";
  }, [form, productById, rt, selectedProductIds, stockByProduct, totals.subtotal]);

  const updateItem = (index, field, value) => setForm((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
  }));

  const removeItem = (index) => setForm((current) => ({
    ...current,
    items: current.items.filter((_, itemIndex) => itemIndex !== index),
  }));

  const submitSale = async () => {
    if (!canManage || saving || validationError) return;
    const payload = {
      locationId: Number(form.locationId),
      sellingOperatorId: Number(form.sellingOperatorId),
      ...(form.clientId ? { clientId: Number(form.clientId) } : {}),
      items: form.items.map((item) => ({ productId: Number(item.productId), quantity: Number(item.quantity) })),
      discountPercent: Number(form.discountPercent || 0),
      discountAmount: Number(form.discountAmount || 0),
    };
    setSaving(true);
    try {
      const sale = await createSpaRetailSale(payload, token, tenant);
      showSnackbar(rt("messages.saved", "Retail sale created and stock updated."), "success");
      setForm((current) => ({ ...emptySale(), locationId: current.locationId }));
      await loadData();
      navigate("/punto_cassa", {
        state: {
          restoredOrderIds: [Number(sale.id)],
          spaRetailSaleId: Number(sale.id),
        },
      });
    } catch (saveError) {
      showSnackbar(saveError?.message || rt("messages.saveError", "Unable to create the retail sale."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      {!canViewResources ? <Alert severity="warning">{rt("messages.resourcePermission", "SPA resource access is required to sell products and verify stock.")}</Alert> : null}
      {error ? <Alert severity="error" action={<Button onClick={() => loadData()}>{rt("actions.retry", "Retry")}</Button>}>{error}</Alert> : null}
      <Paper variant="outlined" sx={{ p: 2 }} component="form" aria-label={rt("formTitle", "New retail sale")} onSubmit={(event) => { event.preventDefault(); submitSale(); }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6" fontWeight={850}>{rt("formTitle", "New retail sale")}</Typography>
              <Typography variant="body2" color="text.secondary">{rt("formHelp", "Sell SPA products, deduct stock, and continue to the cashier for payment.")}</Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshIcon />} disabled={loading || saving} onClick={() => loadData()}>{rt("actions.refresh", "Refresh")}</Button>
          </Stack>
          {loading ? <Alert severity="info" icon={<CircularProgress size={18} />}>{rt("messages.loading", "Loading retail data...")}</Alert> : null}
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="retail-location-label">{rt("fields.location", "Stock location")}</InputLabel>
                <Select labelId="retail-location-label" label={rt("fields.location", "Stock location")} value={form.locationId} disabled={!canManage || !canViewResources || saving}
                  onChange={(event) => setForm((current) => ({ ...current, locationId: event.target.value }))}>
                  {locations.map((location) => <MenuItem key={location.id} value={String(location.id)}>{location.name || location.code}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="retail-operator-label">{rt("fields.operator", "Selling operator")}</InputLabel>
                <Select labelId="retail-operator-label" label={rt("fields.operator", "Selling operator")} value={form.sellingOperatorId} disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, sellingOperatorId: event.target.value }))}>
                  {operators.map((operator) => <MenuItem key={operator.id} value={String(operator.id)}>{personLabel(operator, `Operator ${operator.id}`)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="retail-client-label">{rt("fields.client", "Client (optional)")}</InputLabel>
                <Select labelId="retail-client-label" label={rt("fields.client", "Client (optional)")} value={form.clientId} disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, clientId: event.target.value }))}>
                  <MenuItem value="">{rt("walkIn", "Walk-in customer")}</MenuItem>
                  {clients.map((client) => <MenuItem key={client.id} value={String(client.id)}>{personLabel(client, `Client ${client.id}`)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Typography fontWeight={800}>{rt("itemsTitle", "Products")}</Typography>
            {form.items.map((item, index) => {
              const available = item.productId ? (stockByProduct.get(Number(item.productId)) || 0) : null;
              return <Grid container spacing={1} alignItems="center" key={index}>
                <Grid item xs={12} md={7}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`retail-product-${index}`}>{rt("fields.product", "Product")}</InputLabel>
                    <Select labelId={`retail-product-${index}`} label={rt("fields.product", "Product")} value={item.productId} disabled={!canManage || saving}
                      onChange={(event) => updateItem(index, "productId", event.target.value)}>
                      {products.map((product) => <MenuItem key={product.id} value={String(product.id)} disabled={selectedProductIds.includes(Number(product.id)) && Number(item.productId) !== Number(product.id)}>
                        {product.name} | {t(`spa.products.categories.${product.retailCategory || "OTHER"}`)} | {money(product.sellingPrice)}
                      </MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8} md={3}>
                  <TextField fullWidth size="small" type="number" label={rt("fields.quantity", "Quantity")} value={item.quantity} disabled={!canManage || saving}
                    inputProps={{ min: 0.000001, step: 1 }} onChange={(event) => updateItem(index, "quantity", event.target.value)}
                    helperText={available == null ? " " : rt("available", "Available: {{value}}", { value: available })} />
                </Grid>
                <Grid item xs={4} md={2}>
                  <Tooltip title={rt("actions.removeItem", "Remove product")}><span><IconButton color="error" disabled={!canManage || saving || form.items.length === 1}
                    onClick={() => removeItem(index)}><DeleteOutlineIcon /></IconButton></span></Tooltip>
                </Grid>
              </Grid>;
            })}
            <Button sx={{ alignSelf: "flex-start" }} startIcon={<AddIcon />} disabled={!canManage || saving || form.items.length >= 100}
              onClick={() => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }))}>{rt("actions.addItem", "Add product")}</Button>
          </Stack>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label={rt("fields.discountPercent", "Discount %")}
              value={form.discountPercent} disabled={!canManage || saving} inputProps={{ min: 0, max: 100, step: 0.01 }} onChange={(event) => setForm((current) => ({ ...current, discountPercent: event.target.value }))} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" type="number" label={rt("fields.discountAmount", "Discount amount")}
              value={form.discountAmount} disabled={!canManage || saving} inputProps={{ min: 0, step: 0.01 }} onChange={(event) => setForm((current) => ({ ...current, discountAmount: event.target.value }))} /></Grid>
            <Grid item xs={12} md={6}><Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip label={`${rt("totals.gross", "Gross")}: ${money(totals.gross)}`} />
              <Chip label={`${rt("totals.discount", "Discount")}: ${money(totals.percentDiscount + Number(form.discountAmount || 0))}`} />
              <Chip color="warning" label={`${rt("totals.total", "Total")}: ${money(totals.total)}`} />
            </Stack></Grid>
          </Grid>
          {validationError && form.items.some((item) => item.productId) ? <Alert severity="warning">{validationError}</Alert> : null}
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PointOfSaleIcon />}
            disabled={!canManage || !canViewResources || loading || stockLoading || saving || Boolean(validationError)} sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}>
            {rt("actions.createAndCheckout", "Create sale and open cashier")}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Box sx={{ px: 2, pt: 2 }}><Typography variant="h6" fontWeight={850}>{rt("historyTitle", "Retail sales history")}</Typography></Box>
        <TableContainer>
          <Table size="small" aria-label={rt("historyTitle", "Retail sales history")}>
            <TableHead><TableRow>
              <TableCell>{rt("columns.date", "Date")}</TableCell><TableCell>{rt("columns.order", "Order")}</TableCell>
              <TableCell>{rt("columns.client", "Client")}</TableCell><TableCell>{rt("columns.operator", "Operator")}</TableCell>
              <TableCell>{rt("columns.location", "Location")}</TableCell><TableCell>{rt("columns.items", "Products")}</TableCell>
              <TableCell align="right">{rt("columns.discount", "Discount")}</TableCell><TableCell align="right">{rt("columns.total", "Total")}</TableCell>
              <TableCell align="right">{t("spa.fields.actions", { defaultValue: "Actions" })}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {!loading && !sales.length ? <TableRow><TableCell colSpan={9}>{rt("empty", "No retail sales found.")}</TableCell></TableRow> : null}
              {sales.map((sale) => <TableRow key={sale.id} hover>
                <TableCell>{sale.createdAt ? new Date(sale.createdAt).toLocaleString() : "-"}</TableCell>
                <TableCell><Typography fontWeight={800}>#{sale.id}</Typography><Chip size="small" label={sale.status || "OPEN"} /></TableCell>
                <TableCell>{sale.client ? personLabel(sale.client, `#${sale.client.id}`) : rt("walkIn", "Walk-in customer")}</TableCell>
                <TableCell>{personLabel(sale.sellingOperator, "-")}</TableCell>
                <TableCell>{sale.location?.name || sale.location?.code || "-"}</TableCell>
                <TableCell>{(sale.items || []).map((item) => `${item.product?.name || `#${item.product?.id}`} x${item.quantity}`).join(", ") || "-"}</TableCell>
                <TableCell align="right">{money(saleDiscount(sale))}</TableCell>
                <TableCell align="right">{money(sale.total)}</TableCell>
                <TableCell align="right"><Tooltip title={rt("actions.openCashier", "Open cashier")}><IconButton size="small" onClick={() => navigate("/punto_cassa", { state: { restoredOrderIds: [Number(sale.id)], spaRetailSaleId: Number(sale.id) } })}><PointOfSaleIcon fontSize="small" /></IconButton></Tooltip></TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

export default SpaRetailSales;
